import { useEffect, useState } from "react"

import { fetchDocument } from "../api/signing.services"
import type { SigningRefusal } from "../types/signing.types"

interface SigningDocument {
  url: string | null
  blob: Blob | null
  refusal: SigningRefusal | null
  loading: boolean
}

/**
 * The document, fetched once for the whole sitting.
 *
 * Every fetch is a recorded opening, so the disclosure step and the document
 * step asking separately put two openings in the audit trail for one person
 * reading once. The blob URL is revoked when the sitting ends.
 */
export const useSigningDocument = (
  session: string | null,
  enabled: boolean
): SigningDocument => {
  const [url, setUrl] = useState<string | null>(null)
  const [blob, setBlob] = useState<Blob | null>(null)
  const [refusal, setRefusal] = useState<SigningRefusal | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!session || !enabled) return
    let created: string | null = null
    let abandoned = false

    setLoading(true)
    void fetchDocument(session)
      .then((response) => {
        if (abandoned) {
          if (response.data) URL.revokeObjectURL(response.data)
          return
        }
        if (!response.ok || !response.data) {
          setRefusal(response.refusal ?? { reason: "unknown" })
          return
        }
        created = response.data
        setUrl(response.data)
        setBlob(response.blob ?? null)
      })
      .catch(() => {
        if (!abandoned) setRefusal({ reason: "unknown" })
      })
      .finally(() => {
        if (!abandoned) setLoading(false)
      })

    return () => {
      abandoned = true
      if (created) URL.revokeObjectURL(created)
    }
  }, [session, enabled])

  return { url, blob, refusal, loading }
}
